/**
 * ч.832 (рішення власника): правила розпізнавання виписки — у попапі,
 * таблиця з серверною пагінацією (Закон дизайну #1, infinite), фільтр за
 * платником/отримувачем (назва картки, ЄДРПОУ/РНОКПП, IBAN), сортування колонок.
 */
import { useState } from 'react';
import type { ColDef } from 'ag-grid-community';
import { bankImport, type BankMatchRule, type Direction } from '@/api/bankImport';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import InfiniteDocGrid from '@/shared/components/InfiniteDocGrid';

export default function BankImportRulesModal({ orgId, opLabel, cfiName, onClose, onChanged }: {
  orgId: string;
  opLabel: Record<string, string>;
  cfiName: (id: string | null) => string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [q, setQ] = useState('');
  const [direction, setDirection] = useState<Direction | ''>('');
  const { datasource, total } = useInfiniteGridDatasource<BankMatchRule>(
    (limit, offset, sort) => bankImport.rules(orgId, {
      q, direction, limit, offset,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
    }),
    [orgId, q, direction],
  );
  const cols: ColDef<BankMatchRule>[] = [
    { field: 'direction', headerName: 'Напр.', width: 90, sortable: true,
      valueFormatter: (p) => (p.value === 'IN' ? '↓ вхідні' : '↑ вихідні') },
    { colId: 'counterparty', headerName: 'ЄДРПОУ / IBAN', width: 170, sortable: true,
      valueGetter: (p) => p.data?.counterparty_edrpou ?? p.data?.counterparty_iban ?? '' },
    { field: 'contractor_name', headerName: 'Платник / отримувач', flex: 1.4, minWidth: 200, sortable: true,
      valueFormatter: (p) => p.value ?? '—' },
    { field: 'contract_name', headerName: 'Договір', flex: 1.2, minWidth: 160, sortable: false,
      valueFormatter: (p) => p.value ?? '—' },
    { field: 'operation_type', headerName: 'Вид операції', width: 170, sortable: true,
      valueFormatter: (p) => (p.value ? (opLabel[p.value] ?? p.value) : '—') },
    { colId: 'cfi', headerName: 'Стаття ДДС', width: 180, sortable: false,
      valueGetter: (p) => cfiName(p.data?.cash_flow_item_id ?? null) },
    { field: 'hit_count', headerName: 'Спрац.', width: 90, sortable: true, type: 'rightAligned' },
    { field: 'last_used_at', headerName: 'Востаннє', width: 110, sortable: true,
      valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '—') },
  ];
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 1200,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div className="card rules-modal" style={{ width: 1100, maxWidth: '96vw', padding: '1rem' }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Правила розпізнавання{total != null ? ` (${total})` : ''}</h3>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="платник / отримувач: назва, ЄДРПОУ, IBAN"
            style={{ flex: 1, minWidth: 260 }} autoFocus />
          <select value={direction} onChange={(e) => setDirection(e.target.value as Direction | '')} style={{ width: 130 }}>
            <option value="">усі напрями</option>
            <option value="IN">↓ вхідні</option>
            <option value="OUT">↑ вихідні</option>
          </select>
          <button type="button" onClick={onClose}>закрити</button>
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.4rem' }}>
          Памʼять розбору: що ви підтвердили для платника/отримувача (ЄДРПОУ або IBAN) — контрагент,
          договір, вид операції, стаття ДДС — застосовується до наступних виписок. «×» — видалити правило.
        </div>
        <InfiniteDocGrid<BankMatchRule>
          datasource={datasource}
          columnDefs={cols}
          storageKey="bank-import-rules"
          getRowId={(r) => r.id}
          deleteRow={{ remove: async (id) => { await bankImport.deleteRule(id); onChanged(); }, canDelete: () => true }}
        />
      </div>
    </div>
  );
}
